<template>
  <div class="shift-management">
    <div class="container-fluid">
    <div class="header-section">
        <h1>Shift Management</h1>
        <div class="action-buttons">
          <button 
            v-if="$canAccess('ShiftAdd')"
            class="btn btn-primary" 
            @click="$router.push({ name: 'ShiftAdd' })"
          >
            <i class="fas fa-plus-circle"></i> Add Shift
          </button>
          <button
            class="btn btn-secondary ms-2"
            @click="$router.push({ name: 'nurses' })"
          >
            <i class="fas fa-arrow-left"></i> Back to Nurses
          </button>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

       Filters Section 
      <div class="filters-section mb-4">
        <div class="card">
          <div class="card-header bg-light">
            <h5 class="mb-0">Search</h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-12">
                <div class="d-flex gap-2">
                  <div class="d-flex gap-2 w-100">
                    <input
                        type="text"
                        class="form-control form-element mt-0"
                        style="max-width: 300px;"
                        v-model="searchTerm"
                        placeholder="Search shifts by name..."
                    >
                    <select
                        class="form-select form-element"
                        style="max-width: 200px;"
                        v-model="departmentFilter">
                      <option value="all">All Departments</option>
                      <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                        {{ dept.name }}
                      </option>
                    </select>
                    <button class="btn btn-secondary form-element" @click="resetFilters">Reset</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Shifts Table -->
      <div v-if="!loading && shifts && shifts.length > 0" class="shift-table-container">
        <table class="table table-striped table-hover table-bordered text-start w-100">
        <thead>
            <tr>
              <th @click="sortBy('name')">
                Name 
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('department.name')">
                Department
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('start_time')">
                Start Time
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('end_time')">
                End Time
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('duration_minutes')">
                Duration
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('is_overnight')">
                Overnight
                <i class="fas fa-sort"></i>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="shift in paginatedShifts" :key="shift.id">
              <td>{{ shift.name }}</td>
              <td>{{ shift.department?.name || 'Not Assigned' }}</td>
              <td>{{ $convertTo12Hour(shift.start_time) }}</td>
              <td>{{ $convertTo12Hour(shift.end_time) }}</td>
              <td>{{ formatDuration(shift.duration_minutes) }}</td>
              <td>
                <span :class="shift.is_overnight ? 'badge bg-info' : 'badge bg-secondary'">
                  {{ shift.is_overnight ? 'Yes' : 'No' }}
                </span>
              </td>
              <td>
                <div class="btn-group">
                  <button 
                    v-if="$canAccess('ShiftEdit')"
                    class="btn btn-sm btn-outline-primary" 
                    @click="editShift(shift.id)"
                  >
                   Edit Shift
                  </button>
                  <button
                      v-if="$canAccess('ShiftView')"
                    class="btn btn-sm btn-outline-info" 
                    @click="viewShiftDetails(shift.id)"
                  >
                    Detail
                  </button>
                  <button 
                    v-if="$canAccess('ShiftToggle')"
                    class="btn btn-sm" 
                    :class="shift.is_active ? 'btn-outline-danger' : 'btn-outline-success'"
                    @click="toggleStatus(shift.id)"
                  >
                    <i :class="shift.is_active ? 'fas fa-toggle-off' : 'fas fa-toggle-on'"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination-container">
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="setPage(currentPage - 1)">Previous</button>
              </li>
              <li 
                v-for="page in totalPages" 
                :key="page" 
                class="page-item"
                :class="{ active: page === currentPage }"
              >
                <button class="page-link" @click="setPage(page)">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="setPage(currentPage + 1)">Next</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && (!shifts || shifts.length === 0)" class="empty-state">
        <div class="empty-state-content">
          <i class="fas fa-clock fa-3x"></i>
          <h3>No Shifts Found</h3>
          <p>There are no shifts in the system that match your filters, or none have been defined yet.</p>
          <button 
            v-if="$canAccess('ShiftAdd')"
            class="btn btn-primary" 
            @click="$router.push({ name: 'ShiftAdd' })"
          >
            <i class="fas fa-plus-circle"></i> Add Shift
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ShiftView from "@/views/shifts/ShiftView.vue";

export default {
  name: 'ShiftList',
  data() {
    return {
      shifts: [],
      departments: [],
      loading: false,
      error: null,
      searchTerm: '',
      departmentFilter: 'all',
      timeRangeFilter: 'all',
      overnightFilter: 'all',
      sortColumn: 'name',
      sortDirection: 'asc',
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    ShiftView() {
      return ShiftView
    },
    filteredShifts() {
      if (!this.shifts) return [];

      let filtered = [...this.shifts];
      
      // Apply search filter
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        filtered = filtered.filter(shift => 
          shift.name.toLowerCase().includes(searchLower) ||
          (shift.department?.name && shift.department.name.toLowerCase().includes(searchLower))
        );
      }
      
      // Apply department filter
      if (this.departmentFilter !== 'all') {
        filtered = filtered.filter(shift =>
            shift.department_id === this.departmentFilter
        );
      }

      // Apply department filter
      if (this.departmentFilter !== 'all') {
        filtered = filtered.filter(shift => 
          shift.department_id === parseInt(this.departmentFilter)
        );
      }
      
      // Apply overnight filter
      if (this.overnightFilter !== 'all') {
        const isOvernight = this.overnightFilter === 'yes';
        filtered = filtered.filter(shift => shift.is_overnight === isOvernight);
      }
      
      // Apply time range filter
      if (this.timeRangeFilter !== 'all') {
        filtered = filtered.filter(shift => {
          const startHour = this.getHourFromTimeString(shift.start_time);
          
          switch(this.timeRangeFilter) {
            case 'morning':
              return startHour >= 5 && startHour < 12;
            case 'afternoon':
              return startHour >= 12 && startHour < 17;
            case 'evening':
              return startHour >= 17 && startHour < 22;
            case 'night':
              return startHour >= 22 || startHour < 5;
            default:
              return true;
          }
        });
      }
      
      // Apply sorting
      filtered.sort((a, b) => {
        let valueA = this.getSortValue(a, this.sortColumn);
        let valueB = this.getSortValue(b, this.sortColumn);
        
        // Handle null values
        if (valueA === null) valueA = '';
        if (valueB === null) valueB = '';
        
        // String comparison
        if (typeof valueA === 'string') {
          valueA = valueA.toLowerCase();
          valueB = valueB.toLowerCase();
        }
        
        // Do the actual comparison
        if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      return filtered;
    },
    paginatedShifts() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.filteredShifts.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.filteredShifts.length / this.itemsPerPage);
    }
  },
  methods: {
    async fetchShifts() {
      this.loading = true;
      this.error = null;

      try {
        const response = await this.$api.get('/shifts');
        this.shifts = response.data.data;
      } catch (error) {
        console.error('Error fetching shifts:', error);
        this.error = 'Failed to load shifts. Please try again later.';
        this.shifts = [];
      } finally {
        this.loading = false;
      }
    },
    
    async fetchDepartments() {
      try {
        const response = await this.$api.get('/departments');
        this.departments = response.data.data;
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    },
    
    sortBy(column) {
      if (this.sortColumn === column) {
        // Toggle direction if already sorting by this column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Default to ascending for new column
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    
    getSortValue(obj, path) {
      // Handle nested properties like 'department.name'
      const parts = path.split('.');
      let value = obj;
      
      for (const part of parts) {
        if (value == null) return null;
        value = value[part];
      }
      
      return value;
    },
    
    setPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    
    formatDuration(minutes) {
      if (!minutes && minutes !== 0) return 'N/A';
      
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      
      if (hours === 0) return `${mins}m`;
      if (mins === 0) return `${hours}h`;
      return `${hours}h ${mins}m`;
    },
    
    getHourFromTimeString(timeString) {
      if (!timeString) return 0;
      
      // Handle both formats: "HH:MM:SS" and "HH:MM"
      const parts = timeString.split(':');
      return parseInt(parts[0], 10);
    },
    
    resetFilters() {
      this.searchTerm = '';
      this.departmentFilter = 'all';
      this.timeRangeFilter = 'all';
      this.overnightFilter = 'all';
      this.currentPage = 1;
      this.fetchShifts();
    },
    
    async toggleStatus(shiftId) {
      if (!confirm('Are you sure you want to change this shift\'s status?')) return;
      
      try {
        await this.$api.patch(`/shifts/${shiftId}/toggle`);
        const shift = this.shifts.find(s => s.id === shiftId);
        if (shift) {
          shift.is_active = !shift.is_active;
          this.$toast.success(`Shift ${shift.is_active ? 'activated' : 'deactivated'} successfully`);
        }
      } catch (error) {
        console.error('Error toggling shift status:', error);
        this.$toast.error('Failed to update shift status. Please try again.');
      }
    },
    
    editShift(shiftId) {
      this.$router.push({ 
        name: 'ShiftEdit',
        params: { id: shiftId } 
      });
    },
    
    viewShiftDetails(shiftId) {
      this.$router.push({ 
        name: 'ShiftView', 
        params: { id: shiftId } 
      });
    }
  },
  mounted() {
    this.fetchShifts();
    this.fetchDepartments();
  }
}
</script>

<style scoped>
.shift-management {
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
}

.empty-state-content {
  max-width: 400px;
  margin: 0 auto;
}

.empty-state i {
  color: #6c757d;
  margin-bottom: 15px;
}

.empty-state h3 {
  margin-bottom: 10px;
}

.empty-state p {
  margin-bottom: 20px;
  color: #6c757d;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 50px 0;
}

.badge {
  font-size: 0.85rem;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.filters-section {
  transition: all 0.3s ease;
}

.form-element {
  height: 38px;
  box-sizing: border-box;
}

.form-control, .form-select, .btn {
  line-height: normal;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .action-buttons {
    display: flex;
    width: 100%;
    gap: 10px;
  }
}
</style>
